//@flow
import { createSelector } from 'reselect';
import NumberUtils from 'common/utils/NumberUtils';

import type { PjpMetricResponseModelType } from 'common/models/flow/PjpMetricResponse';
import { PjpGraphResponse } from 'common/models/internal/PjpGraphResponse';
import PjpPolarChartDataBuilder from 'common/entity/pjpMaster/pjpInsights/charts/PjpPolarChartDataBuilder';
import PjpCartesianChartDataBuilder from 'common/entity/pjpMaster/pjpInsights/charts/PjpCartesianChartDataBuilder';
import * as Immutable from 'immutable';
import { PjpMetricValue } from 'common/models/internal/PjpMetricResponse';
import type { PjpMetricGraph } from 'common/models/internal/PjpMetricGraph';
import type { PjpMetricData } from 'common/models/internal/PjpMetricResponse';
import type { PjpInsightsMetadata } from 'common/models/internal/PjpInsightsMetadata';
import type { PjpMetricCardStatusType } from 'common/entity/pjpMaster/pjpDataTypes/insights/PjpMetricCardDataTypes';

export const PjpChartTypeEnum = Object.freeze({
    Pie: 'PIE',
    Bar: 'BAR'
});

export const metricGraphSelector = function (_: Object, props: Object) {
    return props.graph || props.metricGraph;
};

export const isAddMetric = function (_: Object, props: Object) {
    return !!props.isEdit;
};

export const getMetricPlanId = function (_: Object, props: Object) {
    return props.planId || props.params.planId;
};

export const getGraphId = function (_: Object, props: Object) {
    return props.graph.id;
};

export const isPjpMetricMetadataLoading = createSelector(
    [state => state.pjpInsights],
    (pjpInsights: Object) => {
        if (pjpInsights.get('metricsMetadata')) {
            const metricsMetadataLoading = pjpInsights.getIn([
                'metricsMetadata',
                'isLoading'
            ]);
            return metricsMetadataLoading;
        }
        return true;
    }
);

export const getPjpMetricsMetadata = createSelector(
    [state => state.pjpInsights],
    (pjpInsights: Object) => {
        const metricsMetadata = pjpInsights.getIn([
            'metricsMetadata',
            'metadata'
        ]);
        return metricsMetadata;
    }
);

export const getAllPjpInsightsDashboards = createSelector(
    [state => state.pjpInsights],
    (pjpInsights: Object) => {
        const dashboards = pjpInsights.getIn(['allPjpDashboards']);
        if (dashboards) {
            return dashboards.toArray();
        }
        return [];
    }
);

export const isPjpDashboardsLoading = createSelector(
    [state => state.pjpInsights],
    (pjpInsights: Object) => {
        const areDashboardsLoading = pjpInsights.getIn([
            'allPjpDashboards',
            'isLoading'
        ]);

        return !!areDashboardsLoading;
    }
);

export const isMetricDataAvailable = createSelector(
    [state => state.pjpInsights, getMetricPlanId, getGraphId],
    (pjpInsights: Object, planId: string, graphId: string) => {
        if (pjpInsights && pjpInsights.get('pjpMetricData')) {
            return pjpInsights.getIn([
                'pjpMetricData',
                planId,
                graphId,
                'hasData'
            ]);
        }
        return false;
    }
);

export const isMetricDataRefreshing = createSelector(
    [
        state => state.pjpInsights,
        getMetricPlanId,
        metricGraphSelector,
        isAddMetric
    ],
    (
        pjpInsights: Object,
        planId: string,
        metricGraph: PjpMetricGraph,
        isAddMetric: boolean
    ) => {
        if (pjpInsights) {
            if (!isAddMetric) {
                return pjpInsights.getIn([
                    'pjpMetricData',
                    planId,
                    metricGraph.id,
                    'isRefreshing'
                ]);
            }
        }
        return false;
    }
);

export const isMetricDataLoading = createSelector(
    [
        state => state.pjpInsights,
        getMetricPlanId,
        metricGraphSelector,
        isAddMetric
    ],
    (
        pjpInsights: Object,
        planId: string,
        metricGraph: PjpMetricGraph,
        isAddMetric: boolean
    ) => {
        if (pjpInsights) {
            if (!isAddMetric) {
                return pjpInsights.getIn([
                    'pjpMetricData',
                    planId,
                    metricGraph.id,
                    'isLoading'
                ]);
            }
        }
        return pjpInsights.getIn(['configuredMetric', 'isLoading']);
    }
);

export const getExistingPlan = createSelector(
    [
        state => state.pjpInsights,
        isMetricDataAvailable,
        getMetricPlanId,
        metricGraphSelector
    ],
    (
        pjpInsights: Object,
        metricHasData: boolean,
        planId: string,
        metricGraph: PjpMetricGraph
    ) => {
        let metricCardData;
        if (pjpInsights && metricHasData) {
            metricCardData = pjpInsights.getIn([
                'pjpMetricData',
                planId,
                metricGraph.id,
                'entity',
                'metricsData',
                0,
                'existingPlan'
            ]);
        }
        return metricCardData;
    }
);

export const getCurrentPlan = createSelector(
    [
        state => state.pjpInsights,
        isMetricDataAvailable,
        getMetricPlanId,
        metricGraphSelector
    ],
    (
        pjpInsights: Object,
        metricHasData: boolean,
        planId: string,
        metricGraph: PjpMetricGraph
    ) => {
        let metricCardData;
        if (pjpInsights && metricHasData) {
            metricCardData = pjpInsights.getIn([
                'pjpMetricData',
                planId,
                metricGraph.id,
                'entity',
                'metricsData',
                0,
                'currentPlan'
            ]);
        }
        return metricCardData;
    }
);

export const getMetricPlans = createSelector(
    [getCurrentPlan, getExistingPlan],
    (currentPlan: PjpMetricGraph, existingPlan: PjpMetricGraph) => {
        return {
            currentPlan,
            existingPlan
        };
    }
);

export const getConfiguredCurrentPlan = createSelector(
    [state => state.pjpInsights],
    (pjpInsights: Object) => {
        const configuredCurrentPlan = pjpInsights.getIn([
            'configuredMetric',
            'currentPlan'
        ]);
        return configuredCurrentPlan;
    }
);

export const getConfiguredExistingPlan = createSelector(
    [state => state.pjpInsights],
    (pjpInsights: Object) => {
        const configuredExistingPlan = pjpInsights.getIn([
            'configuredMetric',
            'existingPlan'
        ]);
        return configuredExistingPlan;
    }
);

export const isConfiguredPjpMetricLoading = createSelector(
    [state => state.pjpInsights],
    (pjpInsights: Object) => {
        const isConfiguredMetricLoading = pjpInsights.getIn([
            'configuredMetric',
            'isLoading'
        ]);
        return isConfiguredMetricLoading;
    }
);

export const configuredMetricHasData = createSelector(
    [state => state.pjpInsights],
    (pjpInsights: Object) => {
        const configuredMetricHasData = pjpInsights.getIn([
            'configuredMetric',
            'hasData'
        ]);
        return configuredMetricHasData;
    }
);

export const getConfiguredPlanMetric = createSelector(
    [getConfiguredCurrentPlan, getConfiguredExistingPlan],
    (currentPlan: PjpMetricGraph, existingPlan: PjpMetricGraph) => {
        return { currentPlan, existingPlan };
    }
);

export const getMetricsData = createSelector(
    [
        state => state.pjpInsights,
        isMetricDataAvailable,
        getMetricPlanId,
        metricGraphSelector
    ],
    (
        pjpInsights: Object,
        metricHasData: boolean,
        planId: string,
        metricGraph: PjpMetricGraph
    ) => {
        let metricCardData;
        if (pjpInsights && metricHasData) {
            metricCardData = pjpInsights.getIn([
                'pjpMetricData',
                planId,
                metricGraph.id,
                'entity',
                'metricsData'
            ]);
        }
        return metricCardData;
    }
);

export const getConfiguredMetricDataForChart = createSelector(
    [getConfiguredPlanMetric],
    (configuredMetric: any) => {
        const { currentPlan, existingPlan } = configuredMetric;
        const currentPlanData = currentPlan.toJS();
        const existingPlanData = existingPlan?.toJS();
        const updatedCurrentPlanCardData = PjpGraphResponse.fromJS({
            metricsData: [currentPlanData]
        });
        const updatedExistingPlanCardData = existingPlanData
            ? PjpGraphResponse.fromJS({
                  metricsData: [existingPlanData]
              })
            : null;
        return {
            currentPlan: updatedCurrentPlanCardData,
            existingPlan: updatedExistingPlanCardData
        };
    }
);

export const getMetricDataForChart = createSelector(
    [getMetricPlans, getMetricsData],
    (metricPlans: Object, metricData: List<Object>) => {
        const { currentPlan, existingPlan } = metricPlans;
        let updatedExistingPlanCardData;
        let updatedCurrentPlanCardData;
        if (currentPlan) {
            updatedCurrentPlanCardData = metricData.set(0, currentPlan);
        }
        if (existingPlan) {
            updatedExistingPlanCardData = metricData.set(0, existingPlan);
        }
        return {
            currentPlan: PjpGraphResponse.fromJS({
                metricsData: updatedCurrentPlanCardData
                    ? updatedCurrentPlanCardData.toJS()
                    : null
            }),
            existingPlan: existingPlan
                ? PjpGraphResponse.fromJS({
                      metricsData: updatedExistingPlanCardData.toJS()
                  })
                : null
        };
    }
);

export const computeAggregate = function (
    metricGraph: PjpMetricGraph,
    metric: PjpMetricResponseModelType,
    metadataMap: ?Map<string, PjpInsightsMetadata>
) {
    if (!metric) {
        return null;
    }
    const statisticQueryIndex = metricGraph.statisticQueries.get(0);
    const statisticQueryData = metric.metricsData.get(statisticQueryIndex);
    if (!statisticQueryData) {
        throw new Error('Illegal state: statisticQueryData not found!');
    }
    const metricName = statisticQueryData.metric;
    const { metricTotal } = statisticQueryData;
    const aggregate = {};
    aggregate.currentValue =
        NumberUtils.roundOff(
            metricTotal.metricDataRow.get(metricName)?.value || 0
        ) || 0;
    aggregate.metadata = {};
    let metadata = null;
    if (metadataMap && metricName) {
        metadata = metadataMap.get(metricName);
        aggregate.metricUnitType = metadata.metricUnitType;
        aggregate.metricUnit = metadata.metricUnit;
        aggregate.metricUnitLabel = metadata.metricUnitLabel;
    }
    return aggregate;
};

export const decidePjpGraphType = function (currentPlan, existingPlan) {
    const currentMetricDataTeamsSize =
        currentPlan?.metricsData.get(0).metricDataItems?.size || 0;
    const existingMetricDataItemsSize =
        existingPlan?.metricsData.get(0).metricDataItems?.size || 0;
    const maxDataItems =
        currentMetricDataTeamsSize >= existingMetricDataItemsSize
            ? currentMetricDataTeamsSize
            : existingMetricDataItemsSize;
    if (maxDataItems && maxDataItems <= 5) {
        return PjpChartTypeEnum.Pie;
    } else if (maxDataItems && maxDataItems > 5) {
        return PjpChartTypeEnum.Bar;
    }
};

export const getCurrentPlanMetric = createSelector(
    [isAddMetric, getConfiguredCurrentPlan, getCurrentPlan],
    (
        isAddMetric: boolean,
        configuredCurrentPlan,
        currentPlan: PjpMetricData
    ) => {
        if (isAddMetric) {
            return configuredCurrentPlan;
        }
        return currentPlan;
    }
);

export const getCurrentPlanChartData = createSelector(
    [getCurrentPlanMetric],
    (currentPlan: any) => {
        const metricDataForChart = [currentPlan.toJS()];

        const updatedCardDataforChart = PjpGraphResponse.fromJS({
            metricsData: metricDataForChart
        });
        return updatedCardDataforChart;
    }
);

export const deriveSelectors = (chartData: ChartType) => {
    if (
        [PjpChartTypeEnum.Bar, PjpChartTypeEnum.Line].indexOf(chartData.type) <
        0
    ) {
        return null;
    }
    const { data } = chartData;
    if (!data) {
        return null;
    }
    const { currentPlanData, existingPlanData } = data;
    const existingPlanDataKeys = existingPlanData
        ? existingPlanData.dataKeys
        : null;

    const currentPlanDataKeys = currentPlanData
        ? currentPlanData.dataKeys
        : null;
    const dataKeys = currentPlanDataKeys || existingPlanDataKeys;
    if (!dataKeys) {
        return null;
    }
    const values = dataKeys.map(dataKey => {
        return {
            key: dataKey.key,
            label: dataKey.label,
            color: dataKey.color
        };
    });
    return {
        values
    };
};

export const metricGraphMetadataSelector = createSelector(
    [metricGraphSelector, getCurrentPlanChartData, getPjpMetricsMetadata],
    (
        metricGraph: PjpMetricGraph,
        metric: any,
        metadataMap: ?Map<string, PjpInsightsMetadata>
    ) => {
        if (!metric) {
            return null;
        }
        if (!metric) {
            throw new Error('Illegal state: graphQueryData not found!');
        }
        const metricName = metric.metricsData.get(0).metric;
        if (!metadataMap) {
            throw new Error('metadata missing');
        }
        return metadataMap.find(metric => metric.metric === metricName);
    }
);

export const metricUnitSelector = createSelector(
    [metricGraphMetadataSelector],
    (metadata: ?Map<string, PjpInsightsMetadata>) => {
        if (!metadata) {
            return null;
        }
        const { metricUnitType, metricUnit, metricUnitLabel } = metadata;
        return { metricUnitType, metricUnit, metricUnitLabel };
    }
);

export const computeMetricCardStatus = createSelector(
    [isMetricDataLoading, isMetricDataAvailable, isMetricDataRefreshing],
    (isLoading: boolean, hasData: boolean, isRefreshing: boolean) => {
        const status = {
            isLoading,
            isRefreshing,
            hasData
        };
        return status;
    }
);

export const computeConfiguredMetricCardStatus = createSelector(
    [isConfiguredPjpMetricLoading, getCurrentPlanMetric],
    (isLoading: boolean, metric: PjpMetricData) => {
        const status = {
            isLoading,
            isRefreshing: isLoading && !!metric,
            hasData: !!metric
        };
        return status;
    }
);

export const makePolarChartDataForPjp = (
    metricGraph: PjpMetricGraph,
    filters: Object,
    metric: PjpGraphResponse,
    state: Object,
    props: Object
) => {
    const chartDataBuilder = new PjpPolarChartDataBuilder(
        metricGraph,
        filters,
        metric,
        state,
        props
    );
    return chartDataBuilder.auto().build();
};

export const makeCartesianChartDataForPjp = (
    metricGraph: PjpMetricGraph,
    filters: Object,
    metric: PjpGraphResponse,
    state: any,
    props: any
) => {
    const chartDataBuilder = new PjpCartesianChartDataBuilder(
        metricGraph,
        filters,
        metric,
        state,
        props
    );
    return chartDataBuilder.auto().build();
};

export const makeData = (
    graphType: typeof PjpChartTypeEnum,
    metricGraph: PjpMetricGraph,
    filters: Object,
    metric: PjpGraphResponse,
    state: any,
    props: any
) => {
    switch (graphType) {
        case PjpChartTypeEnum.Pie:
            return makePolarChartDataForPjp(
                metricGraph,
                filters,
                metric,
                state,
                props
            );
        case PjpChartTypeEnum.Bar:
            return makeCartesianChartDataForPjp(
                metricGraph,
                filters,
                metric,
                state,
                props
            );
        default:
            return null;
    }
};

export const computeChartData = function (
    graph: PjpMetricGraph,
    graphType: typeof PjpChartTypeEnum,
    metric: PjpGraphResponse,
    units: any,
    state: Object,
    props: Object
) {
    const chartData = {
        type: graphType,
        ...units,
        data: [],
        error: null
    };
    if (!metric || metric.metricsData.get(0).isDataEmpty()) {
        return chartData;
    }

    chartData.data = makeData(graphType, graph, {}, metric, state, props);
    return chartData;
};

export const getConfiguredPjpMetricCard = createSelector(
    [
        state => state,
        (_, props) => props,
        metricGraphSelector,
        computeConfiguredMetricCardStatus,
        getConfiguredMetricDataForChart,
        metricUnitSelector,
        getPjpMetricsMetadata
    ],
    (
        state: any,
        props: any,
        metricGraph: PjpMetricGraph,
        status: PjpMetricCardStatusType,
        configuredMetric: Object,
        units: any,
        metadata: ?Map<string, PjpInsightsMetadata>
    ) => {
        const { existingPlan, currentPlan } = configuredMetric;
        const graphType = decidePjpGraphType(currentPlan, existingPlan);

        const currentPlanAggregate = computeAggregate(
            metricGraph,
            currentPlan,
            metadata
        );

        const currentPlanChartData = computeChartData(
            metricGraph,
            graphType,
            currentPlan,
            units,
            state,
            props
        );
        const metricStatus = status;
        let existingPlanMetricCardData;
        if (existingPlan) {
            const existingPlanAggregate = existingPlan
                ? computeAggregate(metricGraph, existingPlan, metadata)
                : null;
            const existingPlanChartData = computeChartData(
                metricGraph,
                graphType,
                existingPlan,
                units,
                state,
                props
            );
            let existingPlanData = {};
            existingPlanData.existingPlanData = existingPlanChartData.data;
            existingPlanChartData.data = existingPlanData;
            existingPlanMetricCardData = {
                id: metricGraph.id,
                title: metricGraph.title,
                description: metricGraph.description,
                status: metricStatus,
                aggregate: existingPlanAggregate,
                chart: existingPlanChartData,
                selector: deriveSelectors(existingPlanChartData)
            };
        }
        let currentPlanData = {};
        currentPlanData.currentPlanData = currentPlanChartData.data;
        currentPlanChartData.data = currentPlanData;

        const currentPlanMetricCardData = {
            id: metricGraph.id,
            title: metricGraph.title,
            description: metricGraph.description,
            status: metricStatus,
            aggregate: currentPlanAggregate,
            chart: currentPlanChartData,
            selector: deriveSelectors(currentPlanChartData)
        };
        return {
            currentPlanCard: currentPlanMetricCardData,
            existingPlanCard: existingPlanMetricCardData || null
        };
    }
);

//chart data related selectors

export const mergePlanData = (
    state,
    props,
    metricGraph,
    existingPlanData,
    currentPlanData,
    existingPlanDataForChart,
    currentPlanDataForChart,
    metricData,
    units
) => {
    const graphType = decidePjpGraphType(currentPlanData, existingPlanData);
    let updatedMetricGraph;
    let updatedMetricDataItems;

    if (graphType && graphType === 'BAR') {
        const metricQuery = metricGraph.queries.get(0);
        let updatedGroupBy = metricQuery.groupBy;
        updatedGroupBy = updatedGroupBy.splice(1, 0, 'PLAN');
        const updatedMetricQuery = metricQuery.setGroupBy(updatedGroupBy);
        updatedMetricGraph = metricGraph.setQueries(
            Immutable.List([updatedMetricQuery])
        );

        // Modifying Graph till here
        const updatedExistingBeatMetricDataItems = existingPlanDataForChart?.metricDataItems.map(
            metricDataItem => {
                const { metricDataRow } = metricDataItem;
                const planMetricValue = PjpMetricValue.fromJS({
                    value: 'existingPlan',
                    valueDataType: 'STRING'
                });
                const updatedMetricDataRow = metricDataRow.set(
                    'PLAN',
                    planMetricValue
                );

                return metricDataItem.setMetricDataRow(updatedMetricDataRow);
            }
        );
        const updatedCurrentBeatMetricDataItems = currentPlanDataForChart?.metricDataItems.map(
            metricDataItem => {
                const { metricDataRow } = metricDataItem;
                const planMetricValue = PjpMetricValue.fromJS({
                    value: 'currentPlan',
                    valueDataType: 'STRING'
                });
                const updatedMetricDataRow = metricDataRow.set(
                    'PLAN',
                    planMetricValue
                );

                return metricDataItem.setMetricDataRow(updatedMetricDataRow);
            }
        );
        updatedMetricDataItems = updatedExistingBeatMetricDataItems.concat(
            updatedCurrentBeatMetricDataItems
        );
    } else if (graphType && graphType === 'PIE') {
        const existingPlanChartData = {
            type: graphType,
            ...units,
            data: [],
            error: null
        };
        const currentPlanChartData = {
            type: graphType,
            ...units,
            data: [],
            error: null
        };
        if (!metricData) {
            return chartData;
        }
        existingPlanChartData.data = computeChartData(
            metricGraph,
            graphType,
            existingPlanData,
            units,
            state,
            props
        )?.data;
        currentPlanChartData.data = computeChartData(
            metricGraph,
            graphType,
            currentPlanData,
            units,
            state,
            props
        )?.data;
        const updatedData = [];
        updatedData.push(existingPlanChartData.data);
        updatedData.push(currentPlanChartData.data);
        currentPlanChartData.data = updatedData;
        return currentPlanChartData;
    }
    const updatedPlanData = currentPlanDataForChart.setMetricDataItems(
        updatedMetricDataItems
    );
    const updatedMetricArray = [];
    updatedMetricArray.push(updatedPlanData.toJS());

    const updatedMetric = PjpGraphResponse.fromJS({
        metricsData: Immutable.List(updatedMetricArray)
    });

    const chartData = {
        type: graphType,
        ...units,
        data: [],
        error: null
    };
    if (!updatedMetric) {
        return chartData;
    }
    chartData.data = computeChartData(
        updatedMetricGraph,
        graphType,
        updatedMetric,
        units,
        state,
        props
    )?.data;
    return chartData;
};

export const getPjpMetricCard = createSelector(
    [
        state => state,
        (_, props) => props,
        metricGraphSelector,
        computeMetricCardStatus,
        getMetricDataForChart,
        getMetricPlans,
        metricUnitSelector,
        getPjpMetricsMetadata,
        getMetricsData
    ],
    (
        state: Object,
        props: Object,
        metricGraph: PjpMetricGraph,
        status: PjpMetricCardStatusType,
        metricPlans: Object,
        metricPlansChartData: Object,
        units: any,
        metadata: ?Map<string, PjpInsightsMetadata>,
        metricsData: Array<PjpMetricData>
    ) => {
        const currentPlanChartData = metricPlansChartData.currentPlan;
        const existingPlanChartData = metricPlansChartData.existingPlan;

        const { existingPlan, currentPlan } = metricPlans;
        const graphType = decidePjpGraphType(currentPlan, existingPlan);
        let planChartData;
        const currentPlanAggregate = computeAggregate(
            metricGraph,
            currentPlan,
            metadata
        );
        const existingPlanAggregate = computeAggregate(
            metricGraph,
            existingPlan,
            metadata
        );
        if (existingPlan) {
            planChartData = mergePlanData(
                state,
                props,
                metricGraph,
                existingPlan,
                currentPlan,
                existingPlanChartData,
                currentPlanChartData,
                metricsData,
                units
            );
        } else {
            planChartData = computeChartData(
                metricGraph,
                graphType,
                currentPlan,
                units,
                state,
                props
            );
        }

        let updatedPlanChartData = {};
        if (
            existingPlanChartData &&
            planChartData.data &&
            graphType === 'PIE'
        ) {
            updatedPlanChartData.currentPlanData = planChartData.data[0];
            updatedPlanChartData.existingPlanData = planChartData.data[1];
        } else if (currentPlanChartData && planChartData.data) {
            updatedPlanChartData.currentPlanData = planChartData.data;
        } else {
            updatedPlanChartData = null;
        }
        planChartData.data = updatedPlanChartData;

        const metricStatus = status;
        const metricCardData = {
            id: metricGraph.id,
            title: metricGraph.title,
            description: metricGraph.description,
            status: metricStatus,
            aggregate: currentPlanAggregate,
            aggregateExistingValue: existingPlanAggregate,
            chart: planChartData,
            selector: deriveSelectors(planChartData)
        };
        return metricCardData;
    }
);
